import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ExitConfirmationModal from './ExitConfirmationModal';
import { useQuiz } from '../context/QuizContext';
import HubPageLayout from '../components/HubPageLayout';
import FramedPanel from '../components/FramedPanel';
import { calculateScore, responseDistribution, isMultiSelect } from './assessmentScoring';
import { resultsPath } from './assessmentConfig';

// Shared behavioral-screening page for the MoSAIC PSYOPS assessments
// (Catfishing / Romance and Pig Butchering / Crypto). Previously these were
// two ~700-line clones; the only differences are the title, hero image and
// question bank, which now come from assessmentConfig.
//
// Completing the screening navigates to the shared results page, passing the
// score in router state — scores are deliberately never persisted, matching
// the guideline "vulnerability scores are not recorded".
const AssessmentPage = ({ assessment, onQuizStateChange }) => {
  const { title, heroImage, questions, slug } = assessment;
  const navigate = useNavigate();
  const location = useLocation();
  const { setQuizActive } = useQuiz();

  const [quizState, setQuizState] = useState({ active: false, currentQuestion: 0, answers: {} });
  const [showExitModal, setShowExitModal] = useState(false);

  // Restart cleanly when switching between the two assessments.
  useEffect(() => {
    setQuizState({ active: false, currentQuestion: 0, answers: {} });
  }, [slug]);

  // Mirror quiz state into context (sidebar nav guard) and any parent tab shell.
  useEffect(() => {
    setQuizActive(quizState.active);
    if (onQuizStateChange) onQuizStateChange(quizState.active);
  }, [quizState.active, setQuizActive, onQuizStateChange]);

  // Guard browser back / tab close while a screening is in progress.
  useEffect(() => {
    if (!quizState.active) return undefined;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    const handlePopState = () => {
      window.history.pushState(null, '', location.pathname);
      setShowExitModal(true);
    };

    window.history.pushState(null, '', location.pathname);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [quizState.active, location.pathname]);

  const startQuiz = () => setQuizState({ active: true, currentQuestion: 0, answers: {} });
  const resetQuiz = useCallback(() => setQuizState({ active: false, currentQuestion: 0, answers: {} }), []);

  const handleAnswer = (questionIndex, optionIndex) => {
    setQuizState((prev) => {
      const answers = { ...prev.answers };
      if (isMultiSelect(questions[questionIndex])) {
        const current = Array.isArray(answers[questionIndex]) ? answers[questionIndex] : [];
        answers[questionIndex] = current.includes(optionIndex)
          ? current.filter((i) => i !== optionIndex)
          : [...current, optionIndex];
      } else {
        answers[questionIndex] = optionIndex;
      }
      return { ...prev, answers };
    });
  };

  const canProceed = () => {
    const answer = quizState.answers[quizState.currentQuestion];
    if (answer === undefined || answer === null) return false;
    return Array.isArray(answer) ? answer.length > 0 : true;
  };

  const finish = () => {
    const score = calculateScore(questions, quizState.answers);
    const distribution = responseDistribution(questions, quizState.answers);
    // Clear the guard before navigating so the exit modal doesn't fire on a
    // completed screening.
    setQuizState({ active: false, currentQuestion: 0, answers: {} });
    navigate(resultsPath(slug), { state: { score, distribution, slug } });
  };

  const nextQuestion = () => {
    if (quizState.currentQuestion === questions.length - 1) {
      finish();
    } else {
      setQuizState((prev) => ({ ...prev, currentQuestion: prev.currentQuestion + 1 }));
    }
  };

  const prevQuestion = () =>
    setQuizState((prev) => ({ ...prev, currentQuestion: Math.max(0, prev.currentQuestion - 1) }));

  // ---- In-progress screening ------------------------------------------------
  if (quizState.active) {
    const question = questions[quizState.currentQuestion];
    const multi = isMultiSelect(question);
    const currentAnswer = quizState.answers[quizState.currentQuestion];
    const progress = ((quizState.currentQuestion + 1) / questions.length) * 100;

    return (
      <>
        <ExitConfirmationModal
          isOpen={showExitModal}
          onContinue={() => setShowExitModal(false)}
          onExit={() => { setShowExitModal(false); resetQuiz(); }}
        />
        <HubPageLayout>
          <FramedPanel title={title}>
            <div className="px-6 pt-8 pb-6">
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-xl font-bold text-black">
                  Question {quizState.currentQuestion + 1} of {questions.length}
                </h2>
                <span className="text-sm text-gray-600">Progress: {Math.round(progress)}%</span>
              </div>

              <div className="w-full bg-gray-200 rounded-full h-2 mb-8">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>

              <h3 className="text-lg font-bold text-black mb-2">{question.question}</h3>
              {multi && <p className="text-sm text-gray-500 mb-4">Select all that apply.</p>}

              <div className="space-y-3 mt-4">
                {question.options.map((option, index) => {
                  const isSelected = multi
                    ? Array.isArray(currentAnswer) && currentAnswer.includes(index)
                    : currentAnswer === index;
                  return (
                    <button
                      key={option.text}
                      onClick={() => handleAnswer(quizState.currentQuestion, index)}
                      className={`w-full p-4 text-left border-2 transition-all ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 text-blue-800'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      } ${multi ? 'rounded' : 'rounded-lg'}`}
                    >
                      <div className="flex items-center">
                        <div
                          className={`w-4 h-4 border-2 mr-3 flex-shrink-0 ${multi ? 'rounded-sm' : 'rounded-full'} ${
                            isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                          }`}
                        >
                          {isSelected && (
                            <div className={`w-full h-full bg-white transform scale-50 ${multi ? '' : 'rounded-full'}`} />
                          )}
                        </div>
                        {option.text}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="flex justify-between mt-8">
                <button
                  onClick={prevQuestion}
                  disabled={quizState.currentQuestion === 0}
                  className={`px-6 py-3 rounded-lg font-medium ${
                    quizState.currentQuestion === 0
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-500 text-white hover:bg-gray-600'
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={nextQuestion}
                  disabled={!canProceed()}
                  className={`px-6 py-3 rounded-lg font-medium ${
                    !canProceed()
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  {quizState.currentQuestion === questions.length - 1 ? 'View Results' : 'Next'}
                </button>
              </div>
            </div>
          </FramedPanel>
        </HubPageLayout>
      </>
    );
  }

  // ---- Intro / overview -----------------------------------------------------
  return (
    <HubPageLayout>
      <FramedPanel title={title}>
        <div className="px-6 pt-8 pb-6">
          <img src={heroImage} alt={title} className="w-full h-[300px] object-cover" />
        </div>
      </FramedPanel>

      <div className="mt-8">
        <h2 className="text-2xl font-bold text-black mb-4">Overview</h2>
        <p className="text-black text-lg leading-relaxed mb-4">
          This scammer behavioral assessment provides a context-focused scammer threat evaluation of
          early warning indicators of exploitation. Grooming is a gradual process that often starts
          with seemingly harmless interactions and can escalate to more serious behaviors.
        </p>
        <p className="text-black text-lg leading-relaxed">
          This systematic process focuses on psychological manipulation strategies and interaction
          patterns, measuring an individual&rsquo;s responses to provide a probability score.
        </p>
      </div>

      <div className="mt-8">
        <h2 className="text-2xl font-bold text-black mb-4">Assessment Guidelines</h2>
        <ul className="list-disc pl-6 space-y-2 text-black text-lg">
          <li>Responses are private and vulnerability scores are not recorded.</li>
          <li>Answer each statement based on your personal experience.</li>
          <li>You cannot skip questions or return to them later to complete the assessment.</li>
          <li>This assessment is based on known scammer methods and techniques.</li>
        </ul>
      </div>

      <div className="mt-8">
        <h2 className="text-2xl font-bold text-black mb-4">Disclaimer</h2>
        <p className="text-black text-lg leading-relaxed">
          Please read this disclaimer carefully before taking this behavioral assessment. This
          assessment is provided for educational and informational purposes only and is not intended
          as a substitute for professional advice. Should you decide to act upon any information or
          assessment results, you do so at your own risk.
        </p>
      </div>

      <button
        onClick={startQuiz}
        className="w-full text-white py-4 mt-8 text-xl font-bold rounded-xl hover:opacity-90"
        style={{ backgroundColor: '#489CD0' }}
      >
        Start Assessment
      </button>
    </HubPageLayout>
  );
};

export default AssessmentPage;
